export type InventoryOverlapCandidate = {
  id: string;
  name: string;
  facilityChemicals?:
    | {
        facilityId: string;
        reportingYear: number;
        maxAmount?: number | null;
        unit?: string | null;
        facility?: { name: string } | null;
        storageLocations?: { storageLocation?: { description?: string | null } | null }[] | null;
      }[]
    | null;
};

export type InventoryOverlapEntry = {
  chemicalId: string;
  chemicalName: string;
  maxAmount: number | null;
  unit: string | null;
  storageLocationNames: string[];
};

export type InventoryOverlap = {
  facilityId: string;
  facilityName: string;
  reportingYear: number;
  entries: InventoryOverlapEntry[];
  hasIdenticalAmounts: boolean;
};

export function inventoryOverlapKey(
  inventoryOverlap: Pick<InventoryOverlap, "facilityId" | "reportingYear">
): string {
  return `${inventoryOverlap.facilityId}|${inventoryOverlap.reportingYear}`;
}

function computeHasIdenticalAmounts(entries: InventoryOverlapEntry[]): boolean {
  if (entries.length === 0) return false;
  if (entries.some((entry) => entry.maxAmount === null)) return false;

  const [first, ...rest] = entries;
  return rest.every((entry) => entry.maxAmount === first.maxAmount && entry.unit === first.unit);
}

/**
 * Finds (facility, reporting year) pairs where more than one merge candidate
 * has a facility chemical entry. Each such inventory overlap must be resolved
 * (by choosing which chemical's entry to keep) before the merge can proceed.
 */
export function findInventoryOverlaps(candidates: InventoryOverlapCandidate[]): InventoryOverlap[] {
  const entriesByFacilityYear = new Map<
    string,
    {
      facilityId: string;
      facilityName: string;
      reportingYear: number;
      entries: Map<string, InventoryOverlapEntry>;
    }
  >();

  for (const candidate of candidates) {
    for (const facilityChemical of candidate.facilityChemicals ?? []) {
      const key = inventoryOverlapKey(facilityChemical);
      let entry = entriesByFacilityYear.get(key);
      if (!entry) {
        entry = {
          facilityId: facilityChemical.facilityId,
          facilityName: facilityChemical.facility?.name ?? "Unknown facility",
          reportingYear: facilityChemical.reportingYear,
          entries: new Map(),
        };
        entriesByFacilityYear.set(key, entry);
      }
      entry.entries.set(candidate.id, {
        chemicalId: candidate.id,
        chemicalName: candidate.name,
        maxAmount: facilityChemical.maxAmount ?? null,
        unit: facilityChemical.unit ?? null,
        storageLocationNames: Array.from(
          new Set(
            (facilityChemical.storageLocations ?? [])
              .map((storageLocation) => storageLocation.storageLocation?.description)
              .filter((description): description is string => !!description)
          )
        ),
      });
    }
  }

  return Array.from(entriesByFacilityYear.values())
    .filter((entry) => entry.entries.size > 1)
    .map((entry) => {
      const entries = Array.from(entry.entries.values());
      return {
        facilityId: entry.facilityId,
        facilityName: entry.facilityName,
        reportingYear: entry.reportingYear,
        entries,
        hasIdenticalAmounts: computeHasIdenticalAmounts(entries),
      };
    })
    .sort(
      (a, b) => a.facilityName.localeCompare(b.facilityName) || a.reportingYear - b.reportingYear
    );
}

/**
 * Counts distinct (facility, reporting year) pairs across all candidates,
 * whether or not they overlap. Used to report how many facility-years merge
 * automatically (i.e. don't require a decision).
 */
export function countDistinctFacilityYears(candidates: InventoryOverlapCandidate[]): number {
  const keys = new Set<string>();
  for (const candidate of candidates) {
    for (const facilityChemical of candidate.facilityChemicals ?? []) {
      keys.add(inventoryOverlapKey(facilityChemical));
    }
  }
  return keys.size;
}

/**
 * Pre-selects a "keep" chemical for inventory overlaps where every
 * candidate's entry already matches (same amount and unit) — there's no
 * meaningful choice to make, so we default to the first entry.
 */
export function initialInventoryOverlapSelections(
  inventoryOverlaps: InventoryOverlap[]
): Record<string, string> {
  const selections: Record<string, string> = {};
  for (const inventoryOverlap of inventoryOverlaps) {
    if (!inventoryOverlap.hasIdenticalAmounts) continue;
    const firstEntry = inventoryOverlap.entries[0];
    if (!firstEntry) continue;
    selections[inventoryOverlapKey(inventoryOverlap)] = firstEntry.chemicalId;
  }
  return selections;
}
